import { hydrateFusedPosition, type FusedPosition } from "./position";

/**
 * Last published fix for one crew member. Shared via the existing snapshot —
 * not a second protocol, not live SimConnect publish, not ADS-B.
 * Missing / invalid fused stays null (honest empty, never a fake lat/lon).
 */
export type CrewPresence = {
  crewId: string;
  displayName: string;
  fused: FusedPosition | null;
  updatedAtUtc: string;
};

function trim(value?: string | null): string {
  return typeof value === "string" ? value.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function hasUsableFix(fused?: FusedPosition | null): fused is FusedPosition {
  if (!fused || !Number.isFinite(fused.lat) || !Number.isFinite(fused.lon)) return false;
  if (Math.abs(fused.lat) > 90 || Math.abs(fused.lon) > 180) return false;
  return true;
}

export function hydrateCrewPresence(raw?: unknown): CrewPresence | null {
  if (!isRecord(raw)) return null;
  const crewId = trim(typeof raw.crewId === "string" ? raw.crewId : "");
  if (!crewId) return null;
  const fused = hydrateFusedPosition(raw.fused as Partial<FusedPosition> | null | undefined);
  return {
    crewId,
    displayName: trim(typeof raw.displayName === "string" ? raw.displayName : "") || crewId,
    fused: hasUsableFix(fused) ? fused : null,
    updatedAtUtc:
      typeof raw.updatedAtUtc === "string" && raw.updatedAtUtc.trim()
        ? raw.updatedAtUtc
        : fused?.atUtc || new Date().toISOString(),
  };
}

export function hydrateCrewPresenceList(raw?: unknown): CrewPresence[] {
  if (!Array.isArray(raw)) return [];
  const out: CrewPresence[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const item = hydrateCrewPresence(row);
    if (!item || seen.has(item.crewId)) continue;
    seen.add(item.crewId);
    out.push(item);
  }
  return out;
}

export function presenceForCrew(list: CrewPresence[] | undefined, crewId: string | null | undefined): CrewPresence | undefined {
  const id = trim(crewId);
  if (!id) return undefined;
  return (list ?? []).find((row) => row.crewId === id);
}

/** Incoming wins on the same crewId. Used when applying a pulled snapshot. */
export function mergeCrewPresence(incoming: CrewPresence[] | undefined, local: CrewPresence[] | undefined): CrewPresence[] {
  const byId = new Map<string, CrewPresence>();
  for (const row of local ?? []) byId.set(row.crewId, row);
  for (const row of incoming ?? []) byId.set(row.crewId, row);
  return [...byId.values()];
}

export function upsertCrewPresence(
  list: CrewPresence[],
  input: { crewId: string; displayName: string; fused: FusedPosition | null; now?: Date },
): CrewPresence[] {
  const crewId = trim(input.crewId);
  if (!crewId) return list;
  const next: CrewPresence = {
    crewId,
    displayName: trim(input.displayName) || crewId,
    fused: hasUsableFix(input.fused) ? input.fused : null,
    updatedAtUtc: input.now?.toISOString() ?? input.fused?.atUtc ?? new Date().toISOString(),
  };
  const idx = list.findIndex((row) => row.crewId === crewId);
  if (idx === -1) return [...list, next];
  return list.map((row, i) => (i === idx ? next : row));
}

/**
 * Publish this desk’s fused fix onto the shared presence list.
 * Other crews on the snapshot are left alone.
 */
export function publishLocalPresence(
  list: CrewPresence[],
  input: { crewId: string | null | undefined; displayName: string; fused: FusedPosition | null; now?: Date },
): CrewPresence[] {
  const crewId = trim(input.crewId);
  if (!crewId) return list;
  return upsertCrewPresence(list, {
    crewId,
    displayName: input.displayName,
    fused: input.fused,
    now: input.now,
  });
}
